import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useForm} from 'react-hook-form';
import {debounce} from 'lodash';
import {toast} from 'react-toastify';
import {t} from '@lingui/core/macro';

import {cityAPI} from 'features/admin/api/cityAPI';
import {zoneAPI} from 'features/admin/api/zoneAPI';
import {countryAPI} from 'features/admin/api/countryAPI';
import ModalBodyWrapper from 'shared/ui/Modal/ModalBodyWrapper';
import FormGroupWithError from 'shared/ui/FormGroup/FormGroupWithError';
import Select from 'shared/ui/Select/Select';
import InputName from 'shared/ui/Input/InputName';
import ModalBlockButton from 'shared/ui/Button/ModalBlockButton/ModalBlockButton';
import {ICity} from 'entities/location';
import {getSelectDefaultValue} from 'features/admin/lib/selectHelper';

import styles from './Cities.module.scss';

interface ICitiesFormProps {
    onSuccess: () => void;
    item?: ICity;
    onClose: () => void;
}

interface ICitiesFormState {
    zone: number;
    country: number | null;
    city: string;
}

const CITY_NAME_REGEX = /^[a-zA-Zа-яА-Я0-9\- ]+$/;
const MIN_CITY_NAME_LENGTH = 2;
const DEBOUNCE_DELAY = 500;

const CitiesForm: React.FC<ICitiesFormProps> = ({item, onSuccess, onClose}) => {
    const defaultValues = useMemo(() => ({
        zone: item?.country.zone.id || undefined,
        country: item?.country.id || null,
        city: item?.name || '',
    }), [item]);

    const {register, control, handleSubmit, formState, watch, setValue} = useForm<ICitiesFormState>({
        defaultValues,
        mode: 'onChange',
    });

    const {isValid, isDirty, errors} = formState;
    const [searchQuery, setSearchQuery] = useState('');
    const [duplicateError, setDuplicateError] = useState('');
    const [isUserTyping, setIsUserTyping] = useState(false);
    const [lastCountryId, setLastCountryId] = useState<number | null>(null);

    const [zoneId, countryId, cityName] = watch(['zone', 'country', 'city']);
    const isNew = !item;

    const {data: zones = []} = zoneAPI.useGetZonesQuery();
    const {data: countries = []} = countryAPI.useGetCountriesQuery(zoneId, {
        skip: !zoneId,
    });
    const {data: foundCities = []} = cityAPI.useSearchCitiesQuery(
        { countryId: countryId || 0, query: searchQuery },
        { skip: !countryId || searchQuery.length < MIN_CITY_NAME_LENGTH },
    );
    const [createCity] = cityAPI.useCreateCityMutation();
    const [updateCity] = cityAPI.useUpdateCityMutation();

    const zoneOptions = useMemo(() =>
        zones.map(zone => ({ value: zone.id, label: zone.name })),
    [zones],
    );

    const countryOptions = useMemo(() =>
        countries.map(country => ({ value: country.id, label: country.name })),
    [countries],
    );

    const filteredCities = useMemo(() =>
        foundCities.filter(city => city.id !== item?.id),
    [foundCities, item?.id],
    );

    const handleCityChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        setIsUserTyping(true);
        setValue('city', e.target.value, { shouldDirty: true, shouldValidate: true });
    }, [setValue]);

    const getCityErrorMessage = useCallback(() => {
        if (duplicateError) return duplicateError;
        if (errors.city?.type === 'required') return 'Название города обязательно';
        if (errors.city?.type === 'minLength') return `Минимальная длина - ${MIN_CITY_NAME_LENGTH} символа`;
        if (errors.city?.type === 'pattern') return 'Допустимы только буквы, цифры и тире';
        return '';
    }, [duplicateError, errors.city]);

    const onSubmit = useCallback(async(formState: ICitiesFormState) => {
        try {
            const selectedCountry = countries.find(c => c.id === formState.country);
            if (!selectedCountry) return;

            const cityData = {
                name: formState.city,
                country: `/api/v1/countries/${selectedCountry.id}`,
            };

            if (isNew) {
                await createCity(cityData).unwrap();
                toast.info(t`Город успешно добавлен`);
            } else {
                await updateCity({ id: item.id, ...cityData }).unwrap();
                toast.info(t`Город успешно обновлен`);
            }

            onSuccess();
        } catch (error: any) {
            const errorMessage = error?.data?.message || t`Произошла ошибка при сохранении города`;
            toast.info(errorMessage);
        }
    }, [countries, isNew, item?.id, createCity, updateCity, onSuccess]);

    useEffect(() => {
        if (countryId && countryId !== lastCountryId && cityName) {
            setSearchQuery(cityName);
            setIsUserTyping(true);
        }
        setLastCountryId(countryId);
    }, [countryId, cityName, lastCountryId]);

    useEffect(() => {
        const debouncedSearch = debounce((value: string) => {
            if (value.length >= MIN_CITY_NAME_LENGTH && (isUserTyping || countryId !== lastCountryId)) {
                setSearchQuery(value);
            }
        }, DEBOUNCE_DELAY);

        debouncedSearch(cityName);
        return () => debouncedSearch.cancel();
    }, [cityName, isUserTyping, countryId, lastCountryId]);

    useEffect(() => {
        if (!searchQuery) return;

        const exactMatch = filteredCities.find(
            city => city.name.toLowerCase() === cityName?.toLowerCase(),
        );
        setDuplicateError(exactMatch ? 'Такой город уже существует в выбранной стране' : '');
    }, [filteredCities, cityName, searchQuery]);

    useEffect(() => {
        if (item && zoneId === item.country.zone.id) return;
        if (zoneId) {
            setValue('country', null);
            setValue('city', '');
            setSearchQuery('');
        }
    }, [zoneId, setValue, item]);

    const isSubmitDisabled = !isValid || (!isNew && !isDirty) || !!duplicateError || !!errors.city;

    const showCitySuggestions =
        isUserTyping &&
        foundCities.length > 0 &&
        cityName?.length >= MIN_CITY_NAME_LENGTH &&
        (!item || cityName.toLowerCase() !== item.name.toLowerCase());

    return (
        <form onSubmit={handleSubmit(onSubmit)} className={styles.form}>
            <ModalBodyWrapper>
                <FormGroupWithError
                    className={styles.formBlock}
                    label="Зона"
                    error={errors.zone?.message}
                >
                    <Select
                        control={control}
                        name="zone"
                        data={zoneOptions}
                        rules={{required: 'Выберите зону'}}
                        defaultValue={defaultValues.zone}
                        cleanable
                    />
                </FormGroupWithError>
                {zoneId  && (
                    <FormGroupWithError
                        className={styles.formBlock}
                        label="Страна"
                        error={errors.country?.message}
                    >
                        <Select
                            control={control}
                            name="country"
                            data={countryOptions}
                            rules={{required: 'Выберите страну'}}
                            defaultValue={getSelectDefaultValue(defaultValues.country)}
                            cleanable
                        />
                    </FormGroupWithError>
                )}
                {countryId  && (
                    <FormGroupWithError
                        className={styles.formBlock}
                        label="Город"
                        error={getCityErrorMessage()}
                    >
                        <InputName
                            control={control}
                            name="city"
                            placeholder="Введите название города"
                            register={register}
                            onChange={handleCityChange}
                            rules={{
                                required: true,
                                pattern: CITY_NAME_REGEX,
                                minLength: MIN_CITY_NAME_LENGTH,
                            }}
                            defaultValue={cityName}
                        />
                        {showCitySuggestions && (
                            <div className={styles.suggestions}>
                                <div className={styles.suggestionsTitle}>Найдены города:</div>
                                {filteredCities.map(city => (
                                    <div key={city.id} className={styles.foundCity}>
                                        {city.name}
                                    </div>
                                ))}
                            </div>
                        )}
                    </FormGroupWithError>
                )}
            </ModalBodyWrapper>
            <ModalBlockButton
                primaryButtonText={isNew ? t`Добавить` : t`Изменить`}
                primaryButtonType="submit"
                primaryButtonDisabled={isSubmitDisabled}
                secondaryButtonOnClick={onClose}
            />
        </form>
    );
};

export default React.memo(CitiesForm);
